import { Dispatch } from 'react';

import {
  Actions as campaignListActions,
  CampaignListReducerAction,
  CampaignListReducerState,
} from '@features/campaigns/list/types/campaignListReducer.ts';
import { CampaignListStoredFilters } from '@features/campaigns/list/types/campaignListStoredFilters.ts';
import { campaignListFilterDefinitionList } from '@features/campaigns/list/utils/campaignListFiltersConfig.ts';

const setOrDeleteUrlSearchParam = (searchParams: URLSearchParams, paramName: string, value?: string) => {
  if (value) {
    searchParams.set(paramName, value);
    return;
  }

  searchParams.delete(paramName);
};

const parseStoredAdvertiserLabels = (
  advertiserLabels: CampaignListStoredFilters['advertiserLabels'],
): Record<string, string> | undefined => {
  if (!advertiserLabels) {
    return undefined;
  }

  if (typeof advertiserLabels === 'string') {
    try {
      return JSON.parse(advertiserLabels) as Record<string, string>;
    } catch {
      return undefined;
    }
  }

  return advertiserLabels;
};

export const buildCampaignListStoredFilters = (state: CampaignListReducerState): CampaignListStoredFilters => {
  const storedFilters: CampaignListStoredFilters = {};

  for (const definition of campaignListFilterDefinitionList) {
    const serializedValue = definition.serializeFromState(state);

    if (serializedValue) {
      storedFilters[definition.key] = serializedValue;
    }
  }

  if (state.filters?.advertiserLabels && Object.keys(state.filters.advertiserLabels).length > 0) {
    storedFilters.advertiserLabels = state.filters.advertiserLabels;
  }

  return storedFilters;
};

export const syncCampaignListFiltersToUrlSearchParams = (
  currentSearchParams: URLSearchParams,
  state: CampaignListReducerState,
): URLSearchParams => {
  const searchParams = new URLSearchParams(currentSearchParams);

  for (const definition of campaignListFilterDefinitionList) {
    setOrDeleteUrlSearchParam(searchParams, definition.key, definition.serializeFromState(state));
  }

  return searchParams;
};

export const hydrateCampaignListFiltersFromStorage = (
  storedFilters: CampaignListStoredFilters,
  dispatch: Dispatch<CampaignListReducerAction>,
  currentSearchParams: URLSearchParams,
): URLSearchParams => {
  for (const definition of campaignListFilterDefinitionList) {
    const storedValue = storedFilters[definition.key];

    if (storedValue && definition.key !== 'advertiser') {
      definition.hydrate(storedValue, dispatch);
    }
  }

  if (storedFilters.advertiser || storedFilters.advertiserLabels) {
    dispatch({
      type: campaignListActions.CHANGE_ADVERTISER_FILTER,
      payload: {
        filters: {
          advertiser: storedFilters.advertiser?.split(','),
          advertiserLabels: parseStoredAdvertiserLabels(storedFilters.advertiserLabels),
        },
      },
    });
  }

  const searchParams = new URLSearchParams(currentSearchParams);

  for (const definition of campaignListFilterDefinitionList) {
    setOrDeleteUrlSearchParam(searchParams, definition.key, storedFilters[definition.key]);
  }

  return searchParams;
};
